import { useCallback, useEffect, useRef } from "react"

import type WaveSurfer from "wavesurfer.js"
import RegionsPlugin, {
  type RegionParams,
} from "wavesurfer.js/dist/plugins/regions.js"

import {
  serializeAudioRegions,
  toAudioMarkerParams,
  toEditableAudioRegionParams,
  type AudioRegionParams,
} from "./audioRegionController"
import {
  layoutAudioRegionLabels,
  styleAudioRegionResizeHandles,
} from "./audioRegionPresentation"
import { useAudioRegionSelection } from "./useAudioRegionSelection"

interface UseAudioRegionSyncOptions {
  compact: boolean
  isReady: boolean
  onRegionPlayed?: (region: AudioRegionParams) => void
  onRegionsChange?: (regions: AudioRegionParams[]) => void
  regions?: RegionParams[]
  useRegionsForEditing: boolean
  wavesurfer: WaveSurfer | null
}

/**
 * Owns the regions plugin and everything that hangs off it: the selection it
 * publishes, the handle styling regions need once drawn, and the effect that
 * keeps the drawn regions in step with the ones the caller passes.
 */
export function useAudioRegionSync({
  compact,
  isReady,
  onRegionPlayed,
  onRegionsChange,
  regions,
  useRegionsForEditing,
  wavesurfer,
}: UseAudioRegionSyncOptions) {
  const regionsPluginRef = useRef<InstanceType<typeof RegionsPlugin> | null>(
    null,
  )
  const isSyncingRegionsRef = useRef(false)
  const regionSelection = useAudioRegionSelection({
    enabled: onRegionsChange !== undefined,
    regionsPluginRef,
  })
  const { clearSelection, handleRegionClicked } = regionSelection

  const styleResizeHandles = useCallback(() => {
    const plugin = regionsPluginRef.current
    if (!plugin) {
      return
    }
    styleAudioRegionResizeHandles(plugin.getRegions())
  }, [])

  // Regions plugin: read-only uses full regions; editable uses zero-length markers (waveform markers)
  useEffect(() => {
    if (!isReady || !wavesurfer || regions === undefined) {
      if (regionsPluginRef.current) {
        regionsPluginRef.current.destroy()
        regionsPluginRef.current = null
        clearSelection()
      }
      return
    }
    const shouldShowRegions =
      regions.length > 0 || onRegionsChange !== undefined
    if (!shouldShowRegions) {
      if (regionsPluginRef.current) {
        regionsPluginRef.current.destroy()
        regionsPluginRef.current = null
        clearSelection()
      }
      return
    }

    let plugin = regionsPluginRef.current
    if (!plugin) {
      plugin = RegionsPlugin.create()
      wavesurfer.registerPlugin(plugin)
      regionsPluginRef.current = plugin
    }

    isSyncingRegionsRef.current = true
    plugin.clearRegions()
    if (onRegionsChange) {
      const convert = useRegionsForEditing
        ? toEditableAudioRegionParams
        : toAudioMarkerParams
      regions.forEach((r) => {
        plugin.addRegion(convert(r))
      })
    } else {
      regions.forEach((r) => {
        plugin.addRegion(r)
      })
    }
    isSyncingRegionsRef.current = false
    // Style the resize handles after regions are rendered
    if (onRegionsChange && useRegionsForEditing) {
      requestAnimationFrame(() => requestAnimationFrame(styleResizeHandles))
    }

    const unsubs: (() => void)[] = []
    if (onRegionPlayed) {
      unsubs.push(plugin.on("region-in", onRegionPlayed))
    }
    if (onRegionsChange) {
      unsubs.push(
        plugin.on("region-updated", () => {
          if (!isSyncingRegionsRef.current) {
            onRegionsChange(serializeAudioRegions(plugin.getRegions()))
          }
        }),
      )
      unsubs.push(
        plugin.on("region-removed", () => {
          if (!isSyncingRegionsRef.current) {
            onRegionsChange(serializeAudioRegions(plugin.getRegions()))
          }
        }),
      )
      unsubs.push(
        plugin.on("region-clicked", (region, event) => {
          handleRegionClicked(region.id, event)
        }),
      )
    }

    // Wavesurfer's avoidOverlapping runs at 10ms per region and sums overlapping heights
    // instead of lane-assigning — producing wrong stacking for multiple simultaneous overlaps.
    // Override it at 20ms with a correct greedy lane assignment using actual bounding boxes.
    let fixTimer: number | undefined
    if (!onRegionsChange && regions.some((r) => r.content)) {
      fixTimer = window.setTimeout(() => {
        const p = regionsPluginRef.current
        if (!p) {
          return
        }

        layoutAudioRegionLabels(p.getRegions(), { compact })
      }, 20)
    }

    return () => {
      window.clearTimeout(fixTimer)
      for (const u of unsubs) {
        u()
      }
    }
  }, [
    isReady,
    onRegionPlayed,
    onRegionsChange,
    regions,
    wavesurfer,
    useRegionsForEditing,
    styleResizeHandles,
    compact,
    clearSelection,
    handleRegionClicked,
  ])

  return { regionSelection, regionsPluginRef, styleResizeHandles }
}
